import { Chip, Grid, Stack, Typography } from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import PageSection from 'components/common/PageSection'
import Icon from 'components/Icon'
import { ObservabilityToolIcon } from 'components/status-checks/common/ObservabilityToolIcon'
import { EditStatusCheckForm } from 'components/status-checks/edit/EditStatusCheckForm'
import { useStatusCheckFormValidationSchemaBuilder } from 'components/status-checks/hooks/use-status-check-form-validation-schema-builder'
import {
  DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME,
  formatStatusCheckTag,
  getDefaultStatusCheckFormValuesFromStatusCheck,
  getMonitorPageUrl,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { StatusCheckDetailsProvider } from 'context/status-checks/StatusCheckDetailsContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useGetDatadogMonitor } from 'queries/monitors/use-get-datadog-monitor'
import { useGetStatusChecksAsServiceStatusChecks } from 'queries/status-checks/use-get-status-checks-as-service-status-checks'
import { getDatadogMonitorIdFromIntegration } from 'utils/status-checks/status-check-integrations-util-datadog'
import { getMessageFromResponseOrError } from 'utils/strings'

import type { ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'

/**
 * This component is necessary to ensure the loading of all External integrations prior to form initialization.
 * TODO: Handle asynchronous form initialization in EditStatusCheckForm
 */
export const EditStatusCheckFormContainer: React.FC<{
  statusCheck: ServiceStatusCheckFromStatusCheck
  serviceName?: string
}> = ({ statusCheck, serviceName }) => {
  const monitorId =
    getDatadogMonitorIdFromIntegration(statusCheck.integration) ?? undefined

  const {
    data: statusChecks,
    error,
    isError
  } = useGetStatusChecksAsServiceStatusChecks({
    retryOnMount: false,
    staleTime: DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME
  })

  // get details of datadog monitors for display
  const isDatadogMonitor =
    statusCheck.integration.observabilityToolType === 'DATADOG' &&
    Boolean(monitorId)
  const domainParam =
    isDatadogMonitor && statusCheck.integration.domain != 'US1'
      ? `&domain=${statusCheck.integration.domain}`
      : ''

  const {
    externalIntegrationsError,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    getAvailableExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()
  const externalIntegrations = getAvailableExternalIntegrations(false)
  const hasFormError = hasErrorExternalIntegrations || isError

  const {
    data: datadogMonitor,
    isFetching: isFetchingDatadogMonitor,
    isError: isErrorFetchingDatadogMonitor
  } = useGetDatadogMonitor(
    monitorId,
    `${statusCheck?.integration?.name}${domainParam}`,
    { enabled: isDatadogMonitor }
  )

  const buildStatusCheckFormValidationSchema =
    useStatusCheckFormValidationSchemaBuilder(
      externalIntegrations,
      /*
       * Exclude the status check being edited to ensure specific validation test criteria
       * are not artificially marked 'invalid' (looking up monitor URL duplication).
       */
      statusChecks?.filter(({ id }) => id !== statusCheck?.id) ?? []
    )

  const form = useForm<StatusCheckFormValues>({
    mode: 'onChange',
    defaultValues: getDefaultStatusCheckFormValuesFromStatusCheck(
      statusCheck,
      externalIntegrations
    ),
    resolver: buildStatusCheckFormValidationSchema
  })

  return (
    <PageSection
      errorMessage={getMessageFromResponseOrError(
        error ?? externalIntegrationsError,
        'Unable to load required data to edit this Health Check. Please try refreshing the page.'
      )}
      hasError={hasFormError}
      isLoading={isFetchingExternalIntegrations}
    >
      <FormProvider {...form}>
        <StatusCheckDetailsProvider serviceName={serviceName}>
          <EditStatusCheckForm
            headerContent={
              isDatadogMonitor &&
              !isFetchingDatadogMonitor &&
              !isErrorFetchingDatadogMonitor &&
              datadogMonitor?.name ? (
                <Stack direction="row" gap={2}>
                  <ObservabilityToolIcon
                    integration={statusCheck.integration}
                  />
                  <Stack gap={1}>
                    <Stack direction="row" gap={1}>
                      <Typography>{datadogMonitor?.name}</Typography>
                      {statusCheck.integration.url && (
                        <MUIExternalLink
                          href={getMonitorPageUrl(
                            statusCheck.integration.url,
                            statusCheck.integration.type
                          )}
                        >
                          <Icon icon="arrowUpRightFromSquare" />
                        </MUIExternalLink>
                      )}
                    </Stack>
                    <Grid container gap={1} p={1} width="100%">
                      {datadogMonitor.tags.map((tag) => (
                        <Grid key={tag} item>
                          <Chip
                            css={{ opacity: 0.7 }}
                            label={formatStatusCheckTag(tag)}
                            size="small"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </Stack>
              ) : null
            }
            statusCheck={statusCheck}
          />
        </StatusCheckDetailsProvider>
      </FormProvider>
    </PageSection>
  )
}
